<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'
import { setTrack } from '@/api/login'
import { docDownloads, onlineDocs } from './data'
import { downloadAideDocument, getAideDocumentUrl } from '@/api/tool'

function handleResourceLink(event, href) {
  const url = new URL(href, window.location.href)
  const endpoint = new URL(getAideDocumentUrl(''), window.location.origin)
  if (url.origin !== endpoint.origin || url.pathname !== endpoint.pathname) return
  event.preventDefault()
  const filename = url.searchParams.get('filename')
  if (filename) return downloadAideDocument(filename)
}

const docDisplayOrder = ['quick-start', 'app-guide', 'pi-h1-guide', 'unirtos-guide', 'downloads', 'faq']
const orderedDocs = computed(() => {
  const orderedIds = new Set(docDisplayOrder)
  const docById = new Map(onlineDocs.map((doc) => [doc.id, doc]))
  return [
    ...docDisplayOrder.map((id) => docById.get(id)).filter(Boolean),
    ...onlineDocs.filter((doc) => !orderedIds.has(doc.id)),
  ]
})

const docNavItems = computed(() => {
  const docById = new Map(orderedDocs.value.map((doc) => [doc.id, doc]))
  return [
    {
      doc: docById.get('quick-start'),
    },
    {
      title: '开发环境搭建',
      desc: 'UniClaw适配平台部署',
      children: ['pi-h1-guide', 'unirtos-guide']
        .map((id) => docById.get(id))
        .filter(Boolean),
    },
    {
      doc: docById.get('app-guide'),
    },
    {
      doc: docById.get('downloads'),
    },
    {
      doc: docById.get('faq'),
    },
  ].filter((item) => item.doc || item.children?.length)
})

const activeDocId = ref(docDisplayOrder[0] || onlineDocs[0]?.id || '')
const contentRef = ref(null)

const activeDoc = computed(() => {
  return onlineDocs.find((doc) => doc.id === activeDocId.value) || orderedDocs.value[0]
})

const downloadGroups = computed(() => {
  const packageItems = docDownloads.filter((item) => item.type !== '工具下载')
  const toolItems = docDownloads.filter((item) => item.type === '工具下载')

  return [
    {
      title: '运行包与固件包',
      desc: '下载 UniClaw Linux 运行包和 UniRTOS 固件包，用于对应平台的部署与验证。',
      items: packageItems,
    },
    {
      title: '工具下载',
      desc: '下载或跳转获取 ADB 与固件烧录工具，完成设备连接、文件传输和固件烧录。',
      items: toolItems,
    },
  ].filter((group) => group.items.length)
})

const activeSectionIds = computed(() => {
  return (activeDoc.value?.sections || []).map((_, index) => (
    `${activeDoc.value.id}-section-${index}`
  ))
})

const tocItems = computed(() => {
  const items = []
  let currentParent = null

  ;(activeDoc.value?.sections || []).forEach((section, index) => {
    const item = {
      section,
      index,
      children: [],
    }

    if (section.tocLevel === 2 && currentParent) {
      currentParent.children.push(item)
      return
    }

    items.push(item)
    currentParent = item
  })

  return items
})

const resetContentScroll = () => {
  nextTick(() => {
    contentRef.value?.scrollTo({ top: 0, behavior: 'smooth' })
  })
}

const track = async (position) => {
  try {
    await setTrack({ position })
  } catch (error) {
    console.warn('UniClaw docs track failed:', error)
  }
}

const getDocTrackPosition = (id) => {
  return id === 'downloads'
    ? 'uniclaw > view_resources'
    : `uniclaw > view_docs > ${id}`
}

const selectDoc = (id) => {
  activeDocId.value = id
  track(getDocTrackPosition(id))

  if (typeof window !== 'undefined') {
    window.history.replaceState(null, '', `#${id}`)
  }

  resetContentScroll()
}

const scrollToSection = (index) => {
  nextTick(() => {
    const target = contentRef.value?.querySelector(`#${activeSectionIds.value[index]}`)
    if (!target || !contentRef.value) return
    contentRef.value.scrollTo({
      top: target.offsetTop - 24,
      behavior: 'smooth',
    })
  })
}

onMounted(() => {
  const hash = window.location.hash.replace('#', '')
  if (onlineDocs.some((doc) => doc.id === hash)) {
    activeDocId.value = hash
  }

  track(hash === 'downloads' ? 'uniclaw > view_resources' : 'uniclaw > docs')
})
</script>

<template>
  <Header design-always hide-banner />
  <main class="uniclaw-page uc-docs-page">
    <div class="uc-doc-page-heading">
      <nav class="uc-doc-breadcrumb" aria-label="面包屑">
        <RouterLink to="/uniclaw">UniClaw</RouterLink>
        <span aria-hidden="true">/</span>
        <span aria-current="page">文档中心</span>
      </nav>
      <h1>UniClaw 文档中心</h1>
      <p>快速入门、开发环境搭建与应用指南，帮助您完成 UniClaw 部署与开发</p>
    </div>
    <section class="uc-doc-frame" aria-label="UniClaw 文档">
      <aside class="uc-doc-sidebar" aria-label="UniClaw 文档导航">
        <div class="uc-doc-brand">
          <strong>UniClaw 文档</strong>
        </div>

        <div class="uc-doc-switch" role="tablist" aria-label="文档分类">
          <div
            v-for="item in docNavItems"
            :key="item.doc?.id || item.title"
            :class="['uc-doc-switch-group', item.children?.length && 'nested']"
          >
            <button
              v-if="item.doc"
              :class="['uc-doc-switch-item', activeDocId === item.doc.id && 'active']"
              type="button"
              role="tab"
              :aria-selected="activeDocId === item.doc.id"
              @click="selectDoc(item.doc.id)"
            >
              <span>{{ item.doc.label }}</span>
              <small>{{ item.doc.nav }}</small>
            </button>

            <template v-else>
              <div class="uc-doc-switch-parent">
                <span>{{ item.title }}</span>
                <small>{{ item.desc }}</small>
              </div>
              <div class="uc-doc-switch-children">
                <button
                  v-for="doc in item.children"
                  :key="doc.id"
                  :class="['uc-doc-switch-item sub', activeDocId === doc.id && 'active']"
                  type="button"
                  role="tab"
                  :aria-selected="activeDocId === doc.id"
                  @click="selectDoc(doc.id)"
                >
                  <span>{{ doc.label }}</span>
                  <small>{{ doc.nav }}</small>
                </button>
              </div>
            </template>
          </div>
        </div>

      </aside>

      <section class="uc-doc-main" aria-live="polite">
        <div ref="contentRef" class="uc-doc-scroll" tabindex="0">
          <header class="uc-doc-titlebar">
            <div class="uc-doc-heading">
              <h2>{{ activeDoc.title }}</h2>
              <p>{{ activeDoc.desc }}</p>
            </div>
            <div class="uc-doc-tags">
              <span v-for="tag in activeDoc.meta" :key="tag">{{ tag }}</span>
            </div>
          </header>

          <section
            v-for="(section, index) in activeDoc.sections"
            :id="activeSectionIds[index]"
            :key="`${activeDoc.id}-${section.heading}`"
            class="uc-doc-block"
          >
            <h2>{{ section.heading }}</h2>
            <p v-for="text in section.body || []" :key="text">{{ text }}</p>

            <ul v-if="section.bullets?.length" class="uc-doc-list">
              <li v-for="item in section.bullets" :key="item">{{ item }}</li>
            </ul>

            <div v-if="section.inlineLinks?.length" class="uc-doc-inline-links">
              <a
                v-for="link in section.inlineLinks"
                :key="link.href"
                :href="link.href"
                @click="handleResourceLink($event, link.href)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ link.label }}
              </a>
            </div>

            <div v-if="section.links?.length" class="uc-doc-link-grid">
              <template v-for="link in section.links" :key="link.href || link.docId">
                <a
                  v-if="link.href"
                  class="uc-doc-link-card"
                  :href="link.href"
                  @click="handleResourceLink($event, link.href)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{{ link.label }}</span>
                  <small>{{ link.desc }}</small>
                </a>
                <button
                  v-else
                  class="uc-doc-link-card"
                  type="button"
                  @click="selectDoc(link.docId)"
                >
                  <span>{{ link.label }}</span>
                  <small>{{ link.desc }}</small>
                </button>
              </template>
            </div>

            <div v-if="section.code" class="uc-doc-code">
              <span>{{ section.code.label }}</span>
              <pre><code>{{ section.code.value }}</code></pre>
            </div>

            <div v-if="section.table" class="uc-doc-table-wrap">
              <table class="uc-doc-table">
                <tbody>
                  <tr
                    v-for="(row, rowIndex) in section.table"
                    :key="`${section.heading}-${rowIndex}`"
                  >
                    <template v-if="rowIndex === 0">
                      <th v-for="cell in row" :key="cell">{{ cell }}</th>
                    </template>
                    <template v-else>
                      <td v-for="cell in row" :key="cell">{{ cell }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="section.downloads" class="uc-doc-downloads">
              <section
                v-for="group in downloadGroups"
                :key="group.title"
                class="uc-download-group"
              >
                <div class="uc-download-group-head">
                  <h3>{{ group.title }}</h3>
                  <p>{{ group.desc }}</p>
                </div>

                <div class="uc-download-grid">
                  <article
                    v-for="item in group.items"
                    :key="item.name"
                    class="uc-card uc-download-card"
                  >
                    <div>
                      <div class="uc-download-head">
                        <span class="uc-download-badge">{{ item.badge }}</span>
                        <span class="uc-card-meta">{{ item.type }}</span>
                      </div>
                      <h3>{{ item.title }}</h3>
                      <p v-if="item.desc">{{ item.desc }}</p>
                    </div>
                    <a
                      v-if="item.available"
                      class="uc-download-action"
                      :href="item.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click="handleResourceLink($event, item.url); track(`uniclaw > view_resources > ${item.name}`)"
                    >
                      {{ item.actionText || '立即下载' }}
                    </a>
                    <button
                      v-else
                      class="uc-download-action"
                      type="button"
                      disabled
                    >
                      {{ item.status }}
                    </button>
                  </article>
                </div>
              </section>
            </div>

            <figure v-if="section.image" class="uc-doc-image">
              <img :src="section.image" :alt="section.caption || section.heading" />
              <figcaption v-if="section.caption">{{ section.caption }}</figcaption>
            </figure>

            <div v-if="section.images?.length" class="uc-doc-image-list">
              <figure
                v-for="item in section.images"
                :key="item.src"
                class="uc-doc-image"
              >
                <img :src="item.src" :alt="item.caption || section.heading" />
                <figcaption v-if="item.caption">{{ item.caption }}</figcaption>
              </figure>
            </div>
          </section>

        </div>
      </section>

      <aside class="uc-doc-toc" aria-label="本页内容">
        <strong>本页内容</strong>
        <nav class="uc-doc-toc-list" aria-label="当前文档目录">
          <div
            v-for="tocItem in tocItems"
            :key="`${activeDoc.id}-toc-${tocItem.section.heading}`"
            class="uc-doc-toc-group"
          >
            <button
              class="uc-doc-toc-parent"
              type="button"
              @click="scrollToSection(tocItem.index)"
            >
              {{ tocItem.section.heading }}
            </button>
            <div v-if="tocItem.children.length" class="uc-doc-toc-children">
              <button
                v-for="child in tocItem.children"
                :key="`${activeDoc.id}-toc-${child.section.heading}`"
                class="uc-doc-toc-child"
                type="button"
                @click="scrollToSection(child.index)"
              >
                {{ child.section.heading }}
              </button>
            </div>
          </div>
        </nav>
      </aside>
    </section>
  </main>
  <DesignFooter />
</template>

<style lang="scss" scoped>
@use './uniclaw.scss';

.uniclaw-page.uc-docs-page {
  --uc-ink: #14233b;
  --uc-muted: #617089;
  --uc-line: #dce2ea;
  --uc-soft: #f4f7fb;
  padding: 32px 24px 64px;
  background: #f4f7fb;
}
.uc-doc-page-heading {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 32px;
  color: #14233b;
  h1 { margin: 32px 0 16px; font-size: 32px; font-weight: 700; line-height: 40px; }
  p { margin: 0; color: #617089; font-size: 16px; line-height: 24px; }
}
.uc-doc-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: #617089;
  font-size: 14px;
  line-height: 24px;
  a { color: inherit; text-decoration: none; }
  a:hover { color: #ed000c; }
}
.uc-docs-page .uc-doc-frame {
  width: 100%;
  max-width: 1200px;
  height: max(620px, calc(100vh - 160px));
  min-height: 620px;
  grid-template-columns: 240px minmax(0, 1fr) 200px;
  border: 1px solid #dce2ea;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
}
.uc-docs-page .uc-doc-sidebar,
.uc-docs-page .uc-doc-toc {
  overflow: visible;
  background: #f8fafc;
}
.uc-docs-page .uc-doc-main { background: #fff; }
.uc-docs-page .uc-doc-scroll {
  overflow-y: auto;
  scroll-margin-top: 24px;
}
.uc-docs-page .uc-doc-block,
.uc-docs-page .uc-doc-downloads { scroll-margin-top: 24px; }
.uc-docs-page .uc-doc-heading h2 {
  margin: 0;
  color: #14233b;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.4;
}
@media (max-width: 1180px) {
  .uc-docs-page .uc-doc-frame { grid-template-columns: 240px minmax(0, 1fr); }
}
@media (max-width: 860px) {
  .uc-docs-page .uc-doc-frame { height: auto; min-height: 0; display: block; }
}
@media (max-width: 640px) {
  .uniclaw-page.uc-docs-page { padding: 24px 16px 40px; }
  .uc-doc-page-heading h1 { font-size: 26px; line-height: 36px; }
}
</style>
